/* eslint-disable prettier/prettier */

'use client';

import { type FC, useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { ChevronDown } from '../Icons/Paths/ChevronDown';
import { ChevronTop } from '../Icons/Paths/ChevronTop';
import { Input } from '../Input/Input';
import { Typography } from '../Typography/Typography';
import DropdownOption from './Components/DropdownOption';
import type { DropdownOptionProps } from './Components/DropdownOption.types';
import type { DropdownProps } from './Dropdown.types';

import styles from './Styles/Default.module.css';

export const Dropdown: FC<DropdownProps> = ({
  Component = 'div',
  value,
  onChange,
  options,
  disabled,
  dataTestId,
  onBlur,
  onClick,
  onKeyDown,
  onFocus,
  label,
  hint,
  inputRef,
  placeholder,
  error,
  inputChange,
  className,
  uncontrolled,
  placeholderOption,
  showLeftLabelIcon,
  isMenuPositionedAbove,
  labelPlacement,
  ...restProps
}) => {
  const [stateValue, setStateValue] = useState<DropdownOptionProps | undefined>(value);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const highlightedOptionRef = useRef<HTMLLIElement>(null);

  const isValueIncludedInOptions = useCallback(
    (option: DropdownOptionProps | undefined) => {
      if (!option) return false;
      return options.some(o => o.value === option.value);
    },
    [options],
  );

  // CONTROLLED Behavior (original)
  useEffect(() => {
    if (options.length === 0 && !uncontrolled) setStateValue(undefined);
    if (value && !isValueIncludedInOptions(value) && !uncontrolled) setStateValue(undefined);
    if (stateValue && !isValueIncludedInOptions(stateValue) && !uncontrolled)
      setStateValue(undefined);
    if (
      (stateValue === undefined || stateValue) &&
      value &&
      isValueIncludedInOptions(value) &&
      !uncontrolled
    )
      setStateValue(value);
  }, [value, options, stateValue, uncontrolled, isValueIncludedInOptions]);

  // UNCONTROLLED Behavior
  useEffect(() => {
    if (uncontrolled) setStateValue(isValueIncludedInOptions(value) ? value : undefined);
  }, [value, uncontrolled, isValueIncludedInOptions]);

  const selectOption = useCallback(
    (option: DropdownOptionProps) => {
      if (onChange) {
        if (option !== stateValue) onChange(option);
      }
      setStateValue(option);
    },
    [onChange, stateValue],
  );

  const onClickHandler = useCallback(() => {
    onClick?.();
    if (!disabled) setIsOpen(nextState => !nextState);
  }, [onClick, disabled, setIsOpen]);

  useEffect(() => {
    if (isOpen) setHighlightedIndex(0);
  }, [isOpen]);

  const isOptionSelection = (option: DropdownOptionProps) => {
    return option?.value === stateValue?.value;
  };

  const onBlurHandler = useCallback(() => {
    onBlur?.();
    setIsOpen(false);
  }, [onBlur, setIsOpen]);

  const classNames = cn(
    styles.container,
    isOpen && styles.isOpen,
    disabled && styles.disabled,
    className,
  );

  const valueClassNames = cn(disabled && 'disabled', styles.value);

  const handleOnKeyDown: React.KeyboardEventHandler<HTMLDivElement> = useCallback(
    e => {
      switch (e.code) {
        case 'Space':
          if (!isOpen) {
            setIsOpen(true);
          } else if (options.length > 0) {
            selectOption(options[highlightedIndex]);
            setIsOpen(false);
          }
          break;
        case 'ArrowUp': {
          e.preventDefault();
          const newValueUp = highlightedIndex === 0 ? options.length - 1 : highlightedIndex - 1;
          setHighlightedIndex(newValueUp);
          break;
        }
        case 'ArrowDown': {
          e.preventDefault();
          const nextValueDown = (highlightedIndex + 1) % options.length;
          setHighlightedIndex(nextValueDown);
          break;
        }
      }

      highlightedOptionRef.current?.scrollIntoView({
        behavior: 'auto',
        block: 'end',
      });
    },
    [highlightedIndex, options, selectOption],
  );

  const renderArrowIcon = () => {
    if (isOpen) {
      return isMenuPositionedAbove ? <ChevronDown /> : <ChevronTop />;
    }
    return isMenuPositionedAbove ? <ChevronTop /> : <ChevronDown />;
  };

  return (
    <Component
      className={classNames}
      onBlur={onBlurHandler}
      onClick={onClickHandler}
      ref={containerRef}
      tabIndex={0}
    >
      <Input
        aria-disabled
        className={valueClassNames}
        data-readonly
        disabled={disabled}
        error={error}
        {...(dataTestId && { dataTestId: `${dataTestId}-dropdown` })}
        fullWidth
        hint={hint}
        label={label}
        labelPlacement={labelPlacement}
        onChange={() => {
          setStateValue(options[highlightedIndex]);
        }}
        onKeyDown={e => {
          if (e.code === 'Enter' && onKeyDown) {
            onKeyDown(e as React.KeyboardEvent<HTMLInputElement>);
          } else {
            handleOnKeyDown(e);
          }
        }}
        onFocus={onFocus}
        placeholder={placeholder}
        leftLabelIcon={value?.leftContent && showLeftLabelIcon ? value.leftContent : null}
        ref={inputRef}
        rightIcon={renderArrowIcon()}
        value={stateValue?.shortLabel ?? stateValue?.label}
        name={dataTestId}
        {...restProps}
      />
      <ul
        className={`${styles.options} ${
          isOpen ? (isMenuPositionedAbove ? styles.menuTopPosition : styles.menuBottomPosition) : ''
        }`}
        data-test-id={dataTestId}
      >
        {placeholderOption && (
          <li className={`${styles.option} ${styles.placeholderOption}`}>
            <Typography variant="subheading3">{placeholderOption}</Typography>
          </li>
        )}
        {options.map((option, index) => {
          const isHighlighted = index === highlightedIndex;
          const isSelected = isOptionSelection(option);
          const hasDesc = !!option.desc;
          return (
            <li
              ref={isHighlighted ? highlightedOptionRef : null}
              className={`${styles.option} ${isSelected ? styles.selected : ''} ${isHighlighted ? styles.highlighted : ''} ${hasDesc ? styles.hasDesc : ''}`}
              key={`${option.value}+${index}`}
              onMouseDown={e => {
                selectOption(option);
                e.stopPropagation();
                setIsOpen(false);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <DropdownOption {...option} />
            </li>
          );
        })}
      </ul>
    </Component>
  );
};

Dropdown.displayName = 'Dropdown';
export type { DropdownOptionProps };
